import React, { useCallback, useMemo, useState } from 'react';

import { Box, Center } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { AGraphNodeProps } from 'components/Visualization/PixiGraph/core/Node';
import {
  PostureGraphContextProps,
  PostureGraphContextProvider,
} from 'components/Visualization/PixiGraph/maps/PostureMap/hooks/usePostureGraphContext';
import { useResourceType } from 'containers/App/hooks/useResourceType';

import { CloudEventsActivityGraphState } from '../../../types';

import { ActivityGraphCanvas } from './ActivityGraphCanvas';
import {
  ACTIVITY_GRAPH_RESOURCE_NODE_SIZE,
  getActivityEdgeNames,
  VULNERABILITY_NODE_TYPES,
} from './utils';

interface ActivityGraphProps {
  activityGraph: CloudEventsActivityGraphState;
  hasSelectedResource: boolean;
}

export const ActivityGraph = ({
  activityGraph,
  hasSelectedResource,
}: ActivityGraphProps) => {
  const { getResourceType } = useResourceType();
  const [showVulnerability, setShowVulnerability] = useState(true);
  const toggleVulnerability = useCallback(
    () => setShowVulnerability(current => !current),
    [],
  );
  const graphContext = useMemo<PostureGraphContextProps>(() => {
    const relationships = activityGraph.data?.relationships ?? [];
    // Relationship endpoint types remain a fallback for older activity responses.
    const resourceTypes = relationships.reduce<Record<string, string>>(
      (types, relationship) => {
        if (relationship.source && relationship.source_type) {
          types[String(relationship.source)] = String(relationship.source_type);
        }
        if (relationship.dest && relationship.dest_type) {
          types[String(relationship.dest)] = String(relationship.dest_type);
        }
        return types;
      },
      {},
    );
    const nodes: AGraphNodeProps[] = (activityGraph.data?.nodes ?? []).map(
      (node, index) => {
        // Generated activity types use boxed String values; Pixi requires primitives.
        const id = String(node.identity ?? node.resource_id ?? index);
        const nativeName = String(
          node.native_name ??
            resourceTypes[id] ??
            node.service ??
            node.native_display_name ??
            '-',
        );

        return {
          ...node,
          id,
          identity: id,
          name: String(
            node.resource_id ??
              node.native_display_name ??
              node.identity ??
              '-',
          ),
          native_name: nativeName,
          native_display_name: String(node.native_display_name ?? nativeName),
          nodeType: nativeName,
          useColoredIcon: true,
          nodeBackgroundOpacity: 0.2,
          nodeBorderColor: customTheme.colors.gray['200'],
          isActivityGraphNode: true,
          resourceNodeSize: VULNERABILITY_NODE_TYPES.has(nativeName)
            ? undefined
            : ACTIVITY_GRAPH_RESOURCE_NODE_SIZE,
          ariksaType: getResourceType(nativeName) ?? {
            cloud_native_name: nativeName,
          },
        };
      },
    );

    const visibleNodes = showVulnerability
      ? nodes
      : nodes.filter(node => !VULNERABILITY_NODE_TYPES.has(node.native_name));
    const visibleNodeIds = new Set(visibleNodes.map(node => node.id));
    const visibleRelationships = relationships
      .filter(relationship => relationship.source && relationship.dest)
      .filter(
        relationship =>
          visibleNodeIds.has(String(relationship.source)) &&
          visibleNodeIds.has(String(relationship.dest)),
      );
    const nodeTypesById = new Map(
      visibleNodes.map(node => [
        node.id,
        String(node.native_name ?? node.nodeType ?? ''),
      ]),
    );
    const relationshipsWithTypes = visibleRelationships.map(relationship => ({
      ...relationship,
      source_type:
        relationship.source_type ??
        nodeTypesById.get(String(relationship.source)),
      dest_type:
        relationship.dest_type ?? nodeTypesById.get(String(relationship.dest)),
    }));
    const edgeNames = getActivityEdgeNames(
      visibleNodes.map(node => node.id),
      relationshipsWithTypes,
    );
    const activityEdgeLabelsByParent = new Map<string, string>();
    visibleRelationships.forEach((relationship, index) => {
      const label = edgeNames[index];
      const parentId = String(relationship.source);
      if (label && !activityEdgeLabelsByParent.has(parentId)) {
        activityEdgeLabelsByParent.set(parentId, label);
      }
    });
    const graphNodes = visibleNodes.map(node => ({
      ...node,
      activityEdgeLabel: activityEdgeLabelsByParent.get(node.id),
    }));

    return {
      nodes: graphNodes,
      edges: visibleRelationships.map((relationship, index) => ({
        source: String(relationship.source),
        dest: String(relationship.dest),
        source_type: relationship.source_type
          ? String(relationship.source_type)
          : undefined,
        dest_type: relationship.dest_type
          ? String(relationship.dest_type)
          : undefined,
        // Render the relationship only for the hovered edge.
        activityEdgeLabel: edgeNames[index],
        activityEdgeColor: customTheme.colors.gray['250'],
        activityEdgeWidth: 1,
        // PostureMap requires an edge name even though activity edges have none.
        name: '',
      })),
      paths: [],
      ordering: [],
      source_items: [],
      isLoading: activityGraph.isLoading,
      isLoaded: activityGraph.isSuccess,
    };
  }, [activityGraph, getResourceType, showVulnerability]);

  return (
    <>
      {!hasSelectedResource ? (
        <Center h="full" color={customTheme.colors.gray['200']}>
          <Box textAlign="center">
            Provide inputs to render
            <br />
            Activity Graph
          </Box>
        </Center>
      ) : activityGraph.isLoading ? (
        <Center h="full">
          <CustomSpinner size="xl" />
        </Center>
      ) : graphContext.nodes.length ? (
        <PostureGraphContextProvider value={graphContext}>
          <ActivityGraphCanvas
            nodeCount={graphContext.nodes.length}
            showVulnerability={showVulnerability}
            toggleVulnerability={toggleVulnerability}
          />
        </PostureGraphContextProvider>
      ) : (
        <Center h="full" color="gray.300">
          No activity found
        </Center>
      )}
    </>
  );
};
